import { defineComponent } from 'vue'

import MutationGeneratePackageUpgradeSummary from '~/apollo/mutations/dependency/generatePackageUpgradeSummary.gql'
import QueryPackageUpgradeSummary from '~/apollo/queries/dependency/pkgUpgradeSummary.gql'
import type { GraphqlMutationResponse, GraphqlQueryResponse } from '~/types/apolloTypes'
import {
  type FetchPackageUpgradeSummaryQueryVariables,
  type GeneratePackageUpgradeSummaryMutationVariables,
  type PackageUpgradeSummary,
  type VulnerabilityRemediationFixPathUpdate
} from '~/types/types'

/**
 * Mixin that provides functionality for fetching and generating package upgrade summaries
 * Used to retrieve information about changes between package versions
 */
export default defineComponent({
  name: 'UpgradeSummaryMixin',
  data() {
    return {
      summary: null as PackageUpgradeSummary | null
    }
  },

  methods: {
    /**
     * Fetches package upgrade summary and updates the component state
     * @param update - Object containing package information and version details
     * @param refetch - Whether to bypass cache and fetch fresh data
     */
    async fetchPackageUpgradeSummary(
      update: VulnerabilityRemediationFixPathUpdate,
      refetch = false
    ) {
      const summary = await this.queryPackageUpgradeSummary(update, refetch)
      if (summary) {
        this.summary = summary
      }
    },

    /**
     * Performs GraphQL query to retrieve package upgrade summary
     * @param update - Object containing package information and version details
     * @param refetch - Whether to bypass cache and fetch fresh data
     * @returns The package upgrade summary or undefined if not found
     */
    async queryPackageUpgradeSummary(
      update: VulnerabilityRemediationFixPathUpdate,
      refetch = false
    ) {
      const args: FetchPackageUpgradeSummaryQueryVariables = {
        packageName: update.package,
        ecosystem: update.ecosystem,
        fromVersion: update.versionFrom,
        toVersion: update.versionTo
      }
      const res: GraphqlQueryResponse = await this.$fetchGraphqlData(
        QueryPackageUpgradeSummary,
        args,
        refetch
      )

      return res.data?.packageUpgradeSummary
    },

    /**
     * Triggers generation of a new package upgrade summary via GraphQL mutation
     * @param args - Mutation variables required for generating the summary
     * @returns GraphQL mutation response
     */
    async doGeneratePackageUpgradeSummary(
      update: VulnerabilityRemediationFixPathUpdate
    ): Promise<GraphqlMutationResponse> {
      const args: GeneratePackageUpgradeSummaryMutationVariables = {
        packageName: update.package,
        ecosystem: update.ecosystem,
        fromVersion: update.versionFrom,
        toVersion: update.versionTo
      }
      return (await this.$applyGraphqlMutation(
        MutationGeneratePackageUpgradeSummary,
        args
      )) as GraphqlMutationResponse
    }
  }
})
